import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { inventoryApi } from '@/api/clients/apiClient'
import type { Inventory, InventoryMetadata, InventoriesPage, CreateInventoryRequest, UpdateInventoryRequest } from '@/api/types'

interface InventoryModalProps {
  initial?: Inventory
  onClose: () => void
  orgId: string
  onSuccess?: (result: Inventory) => void
}

export function InventoryModal({ initial, onClose, orgId, onSuccess }: InventoryModalProps) {
  const queryClient = useQueryClient()
  const isEdit = !!initial
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [tags, setTags] = useState<string[]>(initial?.tags ?? [])
  const [tagInput, setTagInput] = useState('')
  const [metadata, setMetadata] = useState<InventoryMetadata>(initial?.metadata ?? {})
  const [showMeta, setShowMeta] = useState(false)
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: isEdit
      ? (body: UpdateInventoryRequest) => inventoryApi.update(orgId, initial!.id, body)
      : (body: CreateInventoryRequest) => inventoryApi.create(orgId, body),
    onSuccess: (result) => {
      queryClient.setQueryData<InventoriesPage>(['inventories', orgId], (old) => {
        if (!old) return old
        if (isEdit) {
          return { ...old, inventories: old.inventories.map((i) => (i.id === result.id ? result : i)) }
        }
        return { ...old, inventories: [result, ...old.inventories], total: old.total + 1 }
      })
      onSuccess?.(result)
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { setError('Name is required'); return }
    mutation.mutate({ name: name.trim(), description: description.trim() || undefined, tags, metadata })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-float dark:shadow-none dark:border dark:border-border-dark w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark shrink-0">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark">
            {isEdit ? 'Edit Inventory' : 'New Inventory'}
          </h2>
          <button onClick={onClose} className="text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark">
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-error/10 dark:bg-error/15 text-error rounded-xl text-sm">
                <span className="material-icons text-base">error</span>
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Payments Platform"
                autoFocus
                className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Optional description…"
                className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">Tags</label>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
                      {t}
                      <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} className="hover:text-error">
                        <span className="material-icons text-[12px]">close</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault()
                      const val = tagInput.trim().replace(/,+$/, '')
                      if (val && !tags.includes(val)) setTags([...tags, val])
                      setTagInput('')
                    }
                  }}
                  placeholder="Add tag, press Enter"
                  className="flex-1 px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <button
                  type="button"
                  onClick={() => {
                    const val = tagInput.trim()
                    if (val && !tags.includes(val)) setTags([...tags, val])
                    setTagInput('')
                  }}
                  className="px-3 py-2 text-sm rounded-lg hover:bg-background-light dark:hover:bg-background-dark text-text-light dark:text-text-dark"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowMeta(!showMeta)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-text-light dark:text-text-dark hover:bg-background-light dark:hover:bg-background-dark"
              >
                <span>Metadata <span className="text-subtle-light dark:text-subtle-dark font-normal">(optional)</span></span>
                <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">
                  {showMeta ? 'expand_less' : 'expand_more'}
                </span>
              </button>
              {showMeta && (
                <div className="px-4 pb-4 space-y-4 border-t border-border-light dark:border-border-dark">
                  <div className="pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">Identity</p>
                    <div className="grid grid-cols-2 gap-3">
                      {([
                        ['system_name', 'System Name'],
                        ['business_use_case', 'Business Use Case'],
                        ['environment', 'Environment'],
                        ['lifecycle_stage', 'Lifecycle Stage'],
                      ] as [keyof InventoryMetadata, string][]).map(([key, label]) => (
                        <div key={key}>
                          <label className="block text-xs text-subtle-light dark:text-subtle-dark mb-0.5">{label}</label>
                          <input
                            type="text"
                            value={(metadata[key] as string) ?? ''}
                            onChange={(e) => setMetadata({ ...metadata, [key]: e.target.value || undefined })}
                            className="w-full px-2 py-1.5 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">Ownership</p>
                    <div className="grid grid-cols-2 gap-3">
                      {([
                        ['business_owner', 'Business Owner'],
                        ['technology_owner', 'Technology Owner'],
                        ['security_owner', 'Security Owner'],
                        ['support_group', 'Support Group'],
                        ['business_unit', 'Business Unit'],
                        ['department', 'Department'],
                      ] as [keyof InventoryMetadata, string][]).map(([key, label]) => (
                        <div key={key}>
                          <label className="block text-xs text-subtle-light dark:text-subtle-dark mb-0.5">{label}</label>
                          <input
                            type="text"
                            value={(metadata[key] as string) ?? ''}
                            onChange={(e) => setMetadata({ ...metadata, [key]: e.target.value || undefined })}
                            className="w-full px-2 py-1.5 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">Risk Classification</p>
                    <div className="grid grid-cols-2 gap-3">
                      {([
                        ['criticality', 'Criticality'],
                        ['data_classification', 'Data Classification'],
                        ['sla_tier', 'SLA Tier'],
                      ] as [keyof InventoryMetadata, string][]).map(([key, label]) => (
                        <div key={key}>
                          <label className="block text-xs text-subtle-light dark:text-subtle-dark mb-0.5">{label}</label>
                          <input
                            type="text"
                            value={(metadata[key] as string) ?? ''}
                            onChange={(e) => setMetadata({ ...metadata, [key]: e.target.value || undefined })}
                            className="w-full px-2 py-1.5 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                      ))}
                      <div>
                        <label className="block text-xs text-subtle-light dark:text-subtle-dark mb-0.5">Regulatory Scope</label>
                        <input
                          type="text"
                          value={metadata.regulatory_scope?.join(', ') ?? ''}
                          onChange={(e) => {
                            const val = e.target.value
                            const arr = val ? val.split(',').map((s) => s.trim()).filter(Boolean) : undefined
                            setMetadata({ ...metadata, regulatory_scope: arr })
                          }}
                          placeholder="e.g. PCI-DSS, GDPR"
                          className="w-full px-2 py-1.5 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">CMDB</p>
                    <div>
                      <label className="block text-xs text-subtle-light dark:text-subtle-dark mb-0.5">CMDB Reference</label>
                      <input
                        type="text"
                        value={metadata.cmdb_ref ?? ''}
                        onChange={(e) => setMetadata({ ...metadata, cmdb_ref: e.target.value || undefined })}
                        placeholder="CI sys_id or record ID"
                        className="w-full px-2 py-1.5 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 px-6 py-4 border-t border-border-light dark:border-border-dark shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm rounded-lg hover:bg-background-light dark:hover:bg-background-dark text-text-light dark:text-text-dark"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <span className="material-icons text-base animate-spin">sync</span>
                  {isEdit ? 'Saving…' : 'Creating…'}
                </>
              ) : isEdit ? 'Save Changes' : 'Create Inventory'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
